import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { MessageCircle, Save, Send, RefreshCw, Unlink, CheckCircle2, AlertCircle, QrCode, Plus, X } from 'lucide-react';
import api from '../../utils/api';

const MAX_NUMBERS = 10;
const fromApi = (data) => {
  const list = data.admin_whatsapp_numbers || (data.admin_whatsapp_number ? [data.admin_whatsapp_number] : []);
  return list.length ? list.map((n) => `+${n}`) : [''];
};

// Settings → WhatsApp: admin numbers + on/off for approval alerts,
// and linking the server to WhatsApp by scanning a QR code.
export default function WhatsAppSettingsTab() {
  const [numbers, setNumbers] = useState(['']);
  const [enabled, setEnabled] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [conn, setConn] = useState(null);
  const [testPhone, setTestPhone] = useState('');

  useEffect(() => {
    api.get('/settings/whatsapp')
      .then((r) => {
        setNumbers(fromApi(r.data));
        setEnabled(Boolean(r.data.is_whatsapp_approval_alerts_enabled));
      })
      .catch(() => toast.error('Could not load WhatsApp settings'))
      .finally(() => setLoaded(true));
  }, []);

  const loadStatus = useCallback(() => {
    api.get('/settings/whatsapp/status').then((r) => setConn(r.data)).catch(() => {});
  }, []);

  // Poll faster while waiting for a QR scan, slowly otherwise.
  useEffect(() => {
    loadStatus();
    const waiting = !conn || ['starting', 'qr', 'reconnecting', 'logged_out', 'waiting_for_scan'].includes(conn.status);
    const id = setInterval(loadStatus, waiting ? 3000 : 15000);
    return () => clearInterval(id);
  }, [loadStatus, conn?.status]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/settings/whatsapp', {
        admin_whatsapp_numbers: numbers.map((n) => n.trim()).filter(Boolean),
        admin_whatsapp_number: numbers.map((n) => n.trim()).filter(Boolean).join(','), // older backends
        is_whatsapp_approval_alerts_enabled: enabled,
      });
      setNumbers(fromApi(r.data));
      toast.success('WhatsApp settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async (toPhone) => {
    setTesting(true);
    try {
      const r = await api.post('/settings/whatsapp/test', toPhone ? { phone: toPhone } : {});
      toast.success(r.data.message);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const unlink = async () => {
    if (!window.confirm('Unlink WhatsApp from the server? Alerts stop until you scan a new QR code.')) return;
    try {
      const r = await api.post('/settings/whatsapp/reset');
      toast.success(r.data.message);
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unlink failed');
    }
  };

  const statusView = (() => {
    if (!conn) return { tone: 'gray', text: 'Checking connection…' };
    if (!conn.enabled_on_server) return { tone: 'gray', text: 'WhatsApp service is not running on this server' };
    switch (conn.status) {
      case 'connected': return { tone: 'green', text: `Connected${conn.connected_number ? ` as +${conn.connected_number}` : ''}` };
      case 'qr': return { tone: 'amber', text: 'Not linked — scan the QR code below' };
      case 'waiting_for_scan':
      case 'starting': return { tone: 'amber', text: 'Preparing QR code…' };
      case 'reconnecting': return { tone: 'amber', text: 'Reconnecting…' };
      case 'logged_out': return { tone: 'red', text: 'Unlinked from phone — preparing a new QR code…' };
      default: return { tone: 'red', text: `Error: ${conn.error || conn.status}` };
    }
  })();

  const toneClass = {
    green: 'bg-green-50 text-green-700 border-green-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    gray: 'bg-gray-50 text-gray-600 border-gray-200',
  }[statusView.tone];

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Alert settings */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 className="font-bold text-gray-900">WhatsApp Alerts Configuration</h2>
            <p className="text-xs text-gray-500">Admin gets a WhatsApp message whenever something needs approval</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Admin WhatsApp Numbers</label>
            <div className="space-y-2">
              {numbers.map((n, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={n}
                    onChange={(e) => setNumbers((list) => list.map((v, j) => (j === i ? e.target.value : v)))}
                    placeholder="e.g. 98765 43210"
                    inputMode="tel"
                    disabled={!loaded}
                    className="w-full sm:w-72 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  {numbers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setNumbers((list) => list.filter((_, j) => j !== i))}
                      title="Remove this number"
                      className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {numbers.length < MAX_NUMBERS && (
              <button
                type="button"
                onClick={() => setNumbers((list) => [...list, ''])}
                className="mt-2 flex items-center gap-1 text-sm font-medium text-green-700 hover:text-green-800"
              >
                <Plus className="w-4 h-4" /> Add another number
              </button>
            )}
            <p className="text-xs text-gray-400 mt-1">Every number gets each approval alert. 10-digit Indian numbers get +91 added automatically.</p>
          </div>

          <div className="flex items-center justify-between sm:justify-start sm:gap-4">
            <span className="text-sm font-medium text-gray-700">Enable Approval Notifications</span>
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              onClick={() => setEnabled((v) => !v)}
              className={`w-12 h-6 rounded-full transition-colors ${enabled ? 'bg-green-500' : 'bg-gray-200'}`}
            >
              <span className={`block w-5 h-5 bg-white rounded-full shadow transition-transform mt-0.5 ${enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <button
              onClick={save}
              disabled={saving || !loaded}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={() => sendTest()}
              disabled={testing}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Send className="w-4 h-4" /> {testing ? 'Sending…' : 'Send test message'}
            </button>
          </div>
        </div>
      </div>

      {/* Connection / QR */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
            <QrCode className="w-5 h-5 text-gray-600" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-gray-900">Sending WhatsApp Account</h2>
            <p className="text-xs text-gray-500">The WhatsApp account the alerts are sent from</p>
          </div>
          <button onClick={loadStatus} title="Refresh" className="p-2 rounded-lg text-gray-500 hover:bg-gray-100">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        <div className={`flex items-center gap-2 text-sm border rounded-xl px-3 py-2 ${toneClass}`}>
          {statusView.tone === 'green' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {statusView.text}
          {conn?.queued > 0 && <span className="ml-auto text-xs">{conn.queued} alert(s) waiting to send</span>}
        </div>

        {conn?.qr && (
          <div className="mt-4 flex flex-col sm:flex-row gap-5 items-start">
            <img src={conn.qr} alt="WhatsApp QR code" className="w-56 h-56 border border-gray-200 rounded-xl" />
            <ol className="text-sm text-gray-600 list-decimal pl-5 space-y-1">
              <li>Open WhatsApp on the phone that should <b>send</b> the alerts</li>
              <li>Tap <b>⋮ / Settings → Linked devices → Link a device</b></li>
              <li>Point the phone at this QR code</li>
              <li>The status above turns green when linked</li>
            </ol>
          </div>
        )}

        <p className="text-xs text-gray-500 mt-4">
          Tip: link a separate company WhatsApp number here, so the admin receives the alerts as normal messages
          with notifications. WhatsApp doesn't notify you about messages you send to yourself.
        </p>

        {conn?.status === 'connected' && (
          <button onClick={unlink} className="mt-3 flex items-center gap-2 text-sm text-red-600 hover:text-red-700">
            <Unlink className="w-4 h-4" /> Unlink / scan a different account
          </button>
        )}
      </div>

      {/* Delivery check / diagnostics */}
      {conn?.enabled_on_server && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <h2 className="font-bold text-gray-900">Delivery Check</h2>
              <p className="text-xs text-gray-500">Recent messages and whether they reached the phone · engine v{conn.library_version}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <input
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="Any number, e.g. 98765 43210"
              inputMode="tel"
              className="flex-1 min-w-[180px] border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <button
              onClick={() => sendTest(testPhone)}
              disabled={testing || !testPhone.trim()}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Send className="w-4 h-4" /> Send test text
            </button>
          </div>

          {conn.recent_sends?.length ? (
            <ul className="divide-y divide-gray-100 border border-gray-100 rounded-xl text-sm">
              {conn.recent_sends.map((m) => {
                const tick = {
                  error: ['Failed', 'text-red-600'],
                  pending: ['🕓 Pending', 'text-gray-500'],
                  sent: ['🕓 Sending', 'text-gray-500'],
                  server: ['✓ Sent (not delivered yet)', 'text-amber-600'],
                  delivered: ['✓✓ Delivered', 'text-gray-700'],
                  read: ['✓✓ Read', 'text-blue-600'],
                  played: ['✓✓ Read', 'text-blue-600'],
                }[m.status] || [m.status, 'text-gray-500'];
                return (
                  <li key={m.id} className="flex items-center gap-3 px-3 py-2">
                    <span className="text-gray-400 text-xs w-14 shrink-0">{new Date(m.at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                    <span className="flex-1 min-w-0 truncate">+{m.to} · {m.kind === 'document' ? (m.name || 'Document') : 'Text'}</span>
                    <span className={`text-xs font-semibold shrink-0 ${tick[1]}`}>{tick[0]}</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-gray-400">No messages sent since the server last started.</p>
          )}

          {conn.recent_logs?.length > 0 && (
            <details className="mt-4">
              <summary className="text-xs font-semibold text-gray-500 cursor-pointer">Technical log ({conn.recent_logs.length})</summary>
              <pre className="mt-2 max-h-64 overflow-auto bg-gray-50 border border-gray-100 rounded-lg p-2 text-[11px] leading-snug text-gray-600 whitespace-pre-wrap break-all">
                {conn.recent_logs.map((l) => `${l.at.slice(11, 19)} ${l.level.toUpperCase()} ${l.text}`).join(String.fromCharCode(10))}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
